import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, EMPTY, of, switchMap} from 'rxjs';
import {cloneDeep, isEqual, isNumber} from 'lodash';
import {MetaSurveysInfosService} from '@app/services/metas/meta-surveys-infos.service';
import {take} from 'rxjs/operators';
import {
  IAllAnnotatedStatements,
  ICompiledAnnotation,
  IResponseRefForAnnotation,
  IDictionnaries,
  IAnnoDictElement,
  IToRenameRef, IResponseAnnotation
} from '@app/components/projects/surveys/annotation/store/annotations.entitiy';
import {
  AnnotationsUpdateV2Service
} from '@app/components/projects/surveys/annotation/services/annotation-update-v2.service';
import {IQuestionFilterExpr} from '@app/components/projects/surveys/annotation/store/models/annotationState';
import {nanoid} from 'nanoid';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {ISurveyDef} from '@app/services/metas/meta-surveys-infos.objects';
import {Store} from '@ngrx/store';
import {AppState} from '@store/app.states';
import {GetDataUrl, SaveWithUpload} from '@app/components/projects/surveys/annotation/store/annotation.actions';
import {
  AnnotationsRealtimeService
} from '@app/components/projects/surveys/annotation/services/annotations-realtime.service';
import {confirm} from 'devextreme/ui/dialog';

@Injectable({
  providedIn: 'root'
})
export class AnnotationsDataV2Service {

  _surveyId: string;
  set surveyId(value: string) {
    if (value) {
      this.metaSurveysInfosService.getSurveysMeta$(value)
        .pipe(switchMap(data => data ? of(data.surveyDef) : EMPTY), take(1))
        .subscribe(surveyDef => {
          const questions = surveyDef.questions;
          const questionOrder = this.getOrderedQuestions();
          this.questionFilterExpr$.next({
            key: questionOrder.find(qKey => questions[qKey].type !== 'header' && questions[qKey].rowsOrder.some(rKey =>
              Object.values(questions[qKey].rows[rKey].openCols || {}).some(o => o)))
          });
        });
    }
    this._surveyId = value;
  }

  get surveyId(): string {
    return this._surveyId;
  }

  rawResponses$ = new BehaviorSubject<Array<any>>([]);
  dictionary$ = new BehaviorSubject<IDictionnaries>(null);
  actualDictionary$ = new BehaviorSubject<Record<string, IAnnoDictElement>>({});
  annoByUser$ = new BehaviorSubject<IAllAnnotatedStatements>({});
  compiledAnnotations$ = new BehaviorSubject<Record<string, Record<string, ICompiledAnnotation>>>(null);
  questionFilterExpr$ = new BehaviorSubject<IQuestionFilterExpr>(null);
  filteredResponses$ = new BehaviorSubject<Array<IResponseRefForAnnotation>>(null);
  filteredAnnoList$ = new BehaviorSubject<Record<string, ICompiledAnnotation>>(null);
  toRename$ = new BehaviorSubject<Record<string, Array<IToRenameRef>>>(null);
  totalOcc$: Record<string, BehaviorSubject<number>> = {};
  totalOccIsCalculating: Record<string, boolean> = {};
  totalUser$: Record<string, BehaviorSubject<number>> = {};
  totalUserIsCalculating: Record<string, boolean> = {};
  annoAllSet$ = new BehaviorSubject<boolean>(false);

  constructor(
    private store: Store<AppState>,
    private metaSurveysInfosService: MetaSurveysInfosService,
    private annoUpdateV2Service: AnnotationsUpdateV2Service,
    private toastr: ToastrService,
    private translate: TranslateService,
    private annoRealTimeService: AnnotationsRealtimeService
  ) {
    this.annoRealTimeService.annoByUser$.subscribe(data => {
      this.annoByUser$.next(data);
    });
    this.annoRealTimeService.dictionary$.subscribe(data => this.dictionary$.next(data));
    combineLatest([this.annoByUser$, this.rawResponses$, this.questionFilterExpr$])
      .pipe(switchMap((data) => data.every(d => d) ? of(data) : EMPTY))
      .subscribe(([annoByUser, allResponses, questionFilterExpr]) => {
        const allStatement = !questionFilterExpr.statementKey?.length;
        const allRow = !questionFilterExpr.rowKey?.length;
        const fResp = allResponses?.map(r => {
          const response = (r as any)[`responses_${this.surveyId}`]?.[questionFilterExpr.key];
          if (!response) {
            return [];
          }
          return (allRow ? Object.keys(response || {}) : questionFilterExpr.rowKey).map((rKey: string) => {
            return (allStatement ? Object.keys(response[rKey]?.comments || {}) : questionFilterExpr.statementKey)
              .map((sKey: string) => ({
                userId: r.user_id,
                qKey: questionFilterExpr.key,
                rKey,
                sKey,
                comments: response[rKey]?.comments?.[sKey],
                annotations: annoByUser[r.user_id]?.[rKey]?.annotations?.[sKey]?.annoKeys || []
              })).flat();
          }).flat();
        }).flat();
        if (!isEqual(fResp, this.filteredResponses$.value)) {
          this.filteredResponses$.next(fResp);
        }
      });
    combineLatest([this.compiledAnnotations$, this.questionFilterExpr$, this.dictionary$])
      .pipe(switchMap((data) => data.every(d => d) ? of(data) : EMPTY))
      .subscribe(([compiledAnno, questionFilterExpr, dict]) => {
        const allStatement = !questionFilterExpr.statementKey?.length;
        const allRow = !questionFilterExpr.rowKey?.length;
        const qAnno = compiledAnno[questionFilterExpr.key];
        const fAnno = (allRow ? Object.keys(qAnno || {}) : questionFilterExpr.rowKey)
          .reduce((total, rKey) => {
            return (allStatement ? Object.keys(qAnno?.[rKey] || {}) : questionFilterExpr.statementKey)
              .reduce((totRow, sKey) => {
                return Object.values(qAnno?.[rKey]?.[sKey] || {}).reduce((totStat, anno: ICompiledAnnotation) => {
                  if (dict[questionFilterExpr.key]?.[anno.key]) {
                    totStat[anno.key] ??= {
                      key: anno.key,
                      count: 0,
                      label: dict[questionFilterExpr.key][anno.key].label
                    };
                    totStat[anno.key].count += anno.count;
                  }
                  return totStat;
                }, totRow);
              }, total);
          }, {});
        if (!isEqual(fAnno, this.filteredAnnoList$.value)) {
          this.filteredAnnoList$.next(fAnno);
        }
      });
    combineLatest([this.questionFilterExpr$, this.dictionary$])
      .pipe(switchMap((data) => data.every(a => a) ? of(data) : EMPTY))
      .subscribe(data => {
        if (!isEqual(data[1][data[0].key], this.actualDictionary$.value)) {
          this.actualDictionary$.next(data[1][data[0].key] || {});
        }
      });
    combineLatest([this.annoByUser$, this.dictionary$])
      .pipe(switchMap(data => data.every(a => a) ? of(data) : EMPTY))
      .subscribe(([toCompile, dictionary]) => {
        const questionStructure = this.metaSurveysInfosService.getSurveysMeta$(this.surveyId).value?.surveyDef?.questions;
        const existAnno = [];
        const compiled = Object.entries(toCompile as IAllAnnotatedStatements).reduce((mainObj: any, [uId, user]) => {
          Object.entries(user || {}).reduce((questObj, [key, questions]) => {
            if (dictionary[key] !== undefined) {
              questObj[key] ??= {};
              Object.entries(questions || {}).forEach(([rKey, rows]) => {
                questObj[key][rKey] ??= {};
                Object.entries(rows.annotations || {}).forEach(([sKey, statement]) => {
                  questObj[key][rKey][sKey] ??= {};
                  statement.annoKeys?.forEach((aKey: string) => {
                    if (!questionStructure || (questionStructure[key].rowsOrder.includes(rKey) && questionStructure[key].statementsOrder.includes(sKey))) {
                      questObj[key][rKey][sKey][aKey] ??= {key: aKey, count: 0, user: new Set([])};
                      questObj[key][rKey][sKey][aKey].count++;
                      questObj[key][rKey][sKey][aKey].user.add(+uId);
                      existAnno.push(aKey);
                    }
                  });
                });
              });
            }
            return questObj;
          }, mainObj);
          return mainObj;
        }, {});
        if (!isEqual(compiled, this.compiledAnnotations$.value)) {
          const dico = cloneDeep(this.dictionary$.value);
          const toRename = cloneDeep(this.toRename$.value);
          Object.keys(dico).forEach(qKey => {
            Object.keys(dico[qKey]).forEach(annoKey => {
              if (!existAnno.includes(annoKey)) {
                delete dico[qKey][annoKey];
                if (this.toRename$.value && Object.keys(this.toRename$.value).includes(annoKey)) {
                  delete toRename[annoKey];
                }
              }
            });
          });
          if (!isEqual(dico, this.dictionary$.value)) {
            this.dictionary$.next(dico);
          }
          if (!isEqual(toRename, this.toRename$.value)) {
            this.toRename$.next(toRename);
          }
          this.compiledAnnotations$.next(compiled);
        }
      });
  }

  getLastSavedState() {
    confirm(this.translate.instant('Voulez-vous recharger la dernière sauvegarde? Toute les modifications ultérieures à ce point seront supprimer.'),
      this.translate.instant('Charger la dernière sauvegarde')).then((dialogResult) => {
      if (dialogResult) {
        this.store.dispatch(new GetDataUrl({surveyId: +this.surveyId}));
      }
    });
  }

  getTotalOcc$(questionFilter = this.questionFilterExpr$.value) {
    const key = questionFilter.key + questionFilter.statementKey.sort().join() + questionFilter.rowKey.sort().join();
    this.totalOcc$[key] ??= new BehaviorSubject<number>(null);
    const allStatement = !questionFilter.statementKey?.length;
    const allRow = !questionFilter.rowKey?.length;
    if (!isNumber(this.totalOcc$[key].value) && !this.totalOccIsCalculating[key]) {
      this.totalOccIsCalculating[key] = true;
      const compiled = this.compiledAnnotations$.value[questionFilter.key];
      const totalOcc = Object.keys(compiled || {}).reduce((total, rowKey) => {
        return total + ((allRow || questionFilter.rowKey.includes(rowKey)) ?
          Object.keys(compiled[rowKey] || {}).reduce((totRow, statementKey) => {
            return totRow + ((allStatement || questionFilter.statementKey.includes(statementKey)) ?
              Object.values<ICompiledAnnotation>(compiled[rowKey][statementKey] || {})
                .reduce((totStat, anno) => totStat + anno.count, 0) : 0);
          }, 0) : 0);
      }, 0);
      this.totalOccIsCalculating[key] = false;
      this.totalOcc$[key].next(totalOcc);
    }
    return this.totalOcc$[key];
  }

  getTotalUser$(questionFilter = this.questionFilterExpr$.value) {
    const key = questionFilter.key + questionFilter.statementKey.sort().join() + questionFilter.rowKey.sort().join();
    this.totalUser$[questionFilter.key] ??= new BehaviorSubject<number>(null);
    const allStatement = !questionFilter.statementKey?.length;
    const allRow = !questionFilter.rowKey?.length;
    if (!isNumber(this.totalUser$[questionFilter.key].value) && !this.totalUserIsCalculating[key]) {
      this.totalUserIsCalculating[key] = true;
      const totalUser = this.rawResponses$.value.filter(user => {
        return Object.entries<any>(user[`responses_${this.surveyId}`]?.[questionFilter.key] || {})
          .some(row => {
            return (allRow || questionFilter.rowKey.includes(row[0])) &&
              Object.entries<any>(row[1].comments || {}).some(statement => {
                return (allStatement || questionFilter.statementKey.includes(statement[0])) && !!statement[1].length;
              });
          });
      }).length;
      this.totalUserIsCalculating[key] = false;
      this.totalUser$[key]?.next(totalUser);
    }
    return this.totalUser$[questionFilter.key];
  }

  saveAnnotations() {
    this.store.dispatch(new SaveWithUpload({surveyId: this.surveyId}));
  }

  setV8Annotations(newAnno, qKey) {
    const annoByUser = cloneDeep(this.annoByUser$.value);
    const dictionary = cloneDeep(this.dictionary$.value || {});
    const dictByLAbel = Object.values(dictionary?.[qKey] || {})
      .reduce((acc, value) => ({...acc, [value.label]: value.key}), {});
    newAnno.forEach((response) => {
      response?.autoAnnoLabel?.forEach((label) => {
        const key = dictByLAbel[label] || `anno_${nanoid(7)}`;
        if (!dictionary?.[qKey]?.[key]) {
          dictionary[qKey] ??= {};
          dictionary[qKey][key] = {key, label, autoAnno: true};
          dictByLAbel[label] = key;
        }
        annoByUser[response.userId] ??= {};
        annoByUser[response.userId][response.qKey] ??= {};
        annoByUser[response.userId][response.qKey][response.rKey] ??= {annotations: {}};
        annoByUser[response.userId][response.qKey][response.rKey].annotations ??= {};
        annoByUser[response.userId][response.qKey][response.rKey].annotations[response.sKey] ??= {annoKeys: []};
        annoByUser[response.userId][response.qKey][response.rKey].annotations[response.sKey].annoKeys ??= [];
        if (!annoByUser[response.userId][response.qKey][response.rKey].annotations[response.sKey].annoKeys.includes(key)) {
          annoByUser[response.userId][response.qKey][response.rKey].annotations[response.sKey].annoKeys.push(key);

        }
      });
    });

    this.annoRealTimeService.setDictionaryToFB(dictionary).then(_ =>
      this.annoRealTimeService.setAnnotationByUserToFB(annoByUser).then()
    );
  }

  compileOneQuestion(qKey: string) {
    const compiled = this.compiledAnnotations$.value[qKey];
    return Object.keys(compiled || {}).reduce((total, rKey) => {
      return Object.keys(compiled[rKey] || {})
        .reduce((totRow, sKey) => {
          return Object.values(compiled[rKey][sKey] || {}).reduce((totStat, anno: ICompiledAnnotation) => {
            totStat[anno.key] ??= {key: anno.key, count: 0};
            totStat[anno.key].count += anno.count;
            return totStat;
          }, totRow);
        }, total);
    }, {});
  }

  setRawResults(rawResult: Array<any>) {
    if (!isEqual(rawResult, this.rawResponses$.value)) {
      this.rawResponses$.next(rawResult);
    }
  }

  getOrderedQuestions(surveyDef: ISurveyDef = this.metaSurveysInfosService.getSurveysMeta$(this.surveyId)?.value?.surveyDef) {
    if (!surveyDef) {
      return [];
    }
    const pgKey = surveyDef?.structureElements.pagesGroupsIndex[0];
    return surveyDef?.structureElements.pagesGroups.pagesIndex[pgKey]?.reduce((arr, pKey) =>
      arr.concat(surveyDef?.structureElements.pages.questionsGroupsIndex[pKey]
        ?.map(gKey => surveyDef?.structureElements.questionsGroups?.questionsIndex[gKey])), [])
      .reduce((arr, questionArray) => arr.concat(questionArray), []);
  }

  resetAllAnnotations(qKeys?: Array<string>) {
    const user = cloneDeep(this.annoByUser$.value);
    Object.keys(user).forEach(u => {
      Object.keys(user?.[u] || {}).forEach((q: any) => {
        if (!qKeys || qKeys.includes(q)) {
          Object.keys(user[u]?.[q] || {}).forEach((row: any) => {
            if (!user[u][q][row]?.annotations) {
              user[u][q][row].annotations = {};
              Object.keys(user[u][q][row].selected).forEach((s: any) =>
                user[u][q][row].annotations[s] = {
                  ...user[u][q][row].annotations[s],
                  annoKeys: [],
                  locked: false,
                  star: false,
                  sentiment: null
                }
              );
            } else {
              Object.keys(user[u][q][row].annotations).forEach((s: any) =>
                user[u][q][row].annotations[s] = {
                  ...user[u][q][row].annotations[s],
                  annoKeys: [],
                  locked: false,
                  star: false,
                  sentiment: null
                }
              );
            }
          });
        }
      });
    });
    this.annoRealTimeService.setAnnotationByUserToFB(user).then();
    this.annoRealTimeService.setDictionaryToFB(
      Object.keys(this.dictionary$.value).reduce((obj, qKey) => ({
        ...obj, [qKey]: (!qKeys || qKeys.includes(qKey)) ? {} : this.dictionary$.value[qKey]
      }), {})).then();
  }

  renameAnno(annoKey: string, newLabel: string) {
    const dict = cloneDeep(this.dictionary$.value);
    if (dict[this.questionFilterExpr$.value.key]?.[annoKey]) {
      dict[this.questionFilterExpr$.value.key][annoKey].label = newLabel;
      if (!isEqual(dict, this.dictionary$.value)) {
        this.annoRealTimeService.setDictionaryToFB(dict).then();
      }
    }
  }

  setResponsesIndex2024(dictionnary: IDictionnaries, annoByUser: IAllAnnotatedStatements) {
    this.annoRealTimeService.setDictionaryToFB(dictionnary).then();
    this.annoRealTimeService.setAnnotationByUserToFB(annoByUser).then();
    this.annoAllSet$.next(true);

  }

  setResponsesIndexOld(responses: any, version: string) {
    this.metaSurveysInfosService.getSurveysMeta$(this.surveyId)
      .pipe(switchMap(data => data ? of(data.surveyDef) : EMPTY), take(1))
      .subscribe(surveyDef => {
        const {newData, dico} = version === '2022' ?
          this.annoUpdateV2Service.buildNewFormatFrom2022(responses, Object.values(surveyDef.questions)) :
          this.annoUpdateV2Service.buildNewFormatFromOld(responses, Object.values(surveyDef.questions));
        this.annoRealTimeService.setDictionaryToFB(dico).then();
        this.annoRealTimeService.setAnnotationByUserToFB(newData).then();
        this.annoAllSet$.next(true);
      });
  }

  changeQuestionFilterExpr(key: any, props: 'key' | 'statementKey' | 'rowKey') {
    const questionFilterExpr = cloneDeep(this.questionFilterExpr$.value);
    questionFilterExpr[props] = key;
    if (props === 'key') {
      questionFilterExpr.statementKey = null;
      questionFilterExpr.rowKey = null;
    }
    if (!isEqual(questionFilterExpr, this.questionFilterExpr$.value)) {
      this.questionFilterExpr$.next(questionFilterExpr);
    }
  }

  setQuestionFilterExpr(filterDef: IQuestionFilterExpr) {
    if (!isEqual(this.questionFilterExpr$.value, filterDef) && filterDef.key) {
      this.questionFilterExpr$.next(filterDef);
    }
  }

  changeProps(ref: IResponseRefForAnnotation, prop: string, value: any) {
    const responses = cloneDeep(this.annoByUser$.value);
    responses[ref.userId] ??= {};
    responses[ref.userId][ref.qKey] ??= {};
    responses[ref.userId][ref.qKey][ref.rKey] ??= {annotations: {}};
    responses[ref.userId][ref.qKey][ref.rKey].annotations ??= {};
    responses[ref.userId][ref.qKey][ref.rKey].annotations[ref.sKey] ??= {annoKeys: []};
    const resp = responses[ref.userId][ref.qKey][ref.rKey].annotations[ref.sKey];
    resp[prop] = value;
    this.annoRealTimeService.setAnnotationByUserToFB(responses).then();
  }

  deleteAnnoFromAllSelections(annoKey: string, filterDef: IQuestionFilterExpr = this.questionFilterExpr$.value) {
    const responses = cloneDeep(this.annoByUser$.value);
    const dico = cloneDeep(this.dictionary$.value);
    if (filterDef.key) {
      Object.values(responses || {}).forEach((user) => {
        Object.entries(user[filterDef.key] || {}).forEach(([rKey, row]) => {
          if (!filterDef.rowKey?.length || filterDef.rowKey.includes(rKey)) {
            Object.entries(row.annotations || {}).forEach(([sKey, statement]) => {
              if (statement && (!filterDef.statementKey?.length || filterDef.statementKey.includes(sKey))) {
                statement.locked = false;
                statement.annoKeys = statement.annoKeys?.filter(a => a !== annoKey) || [];
                delete dico[filterDef.key][annoKey];
              }
            });
          }
        });
      });
      this.annoRealTimeService.setDictionaryToFB(dico).then();
      this.annoRealTimeService.setAnnotationByUserToFB(responses).then();
    }
  }

  deleteAnnoFromUserId(annoKey: string, comments: Array<IResponseRefForAnnotation>, locked?: boolean) {
    const responses = cloneDeep(this.annoByUser$.value);
    const dico = cloneDeep(this.dictionary$.value);
    if (annoKey) {
      comments.forEach(comment => {
        const resp = responses[comment.userId][comment.qKey][comment.rKey].annotations[comment.sKey];
        resp.annoKeys = resp.annoKeys?.filter(a => a !== annoKey) || [];
        resp.locked = (locked && !!resp?.annoKeys?.length) ?? resp.locked;
      });
      this.annoRealTimeService.setAnnotationByUserToFB(responses).then();
      this.annoRealTimeService.setDictionaryToFB(dico).then();
    }
  }

  deleteAllAnnoFromOneQuestion(lockedToo?: boolean, filterDef: IQuestionFilterExpr = this.questionFilterExpr$.value) {
    const responses = cloneDeep(this.annoByUser$.value);
    const stillThere = [];
    if (filterDef.key) {
      const allStatement = !filterDef.statementKey?.length;
      const allRow = !filterDef.rowKey?.length;
      Object.entries(responses || {}).forEach(([userId, user]) => {
        Object.entries(user[filterDef.key] || {}).forEach(([rKey, row]) => {
          if (!filterDef.rowKey?.length || filterDef.rowKey.includes(rKey) || allRow) {
            Object.entries(row.annotations || {}).forEach(([sKey, statement]) => {
              if ((!filterDef.statementKey?.length || filterDef.statementKey.includes(sKey) || allStatement) &&
                (!statement.locked || lockedToo)) {
                responses[userId][filterDef.key][rKey].annotations[sKey].annoKeys = [];
                responses[userId][filterDef.key][rKey].annotations[sKey].locked = false;
              } else {
                stillThere.concat(statement.annoKeys);
              }
            });
          }
        });
      });
      const dico = cloneDeep(this.dictionary$.value);
      if (lockedToo) {
        dico[filterDef.key] = {};
      } else {
        Object.keys(dico[filterDef.key]).forEach(annoKey => {
          if (!stillThere.includes(annoKey)) {
            delete dico[filterDef.key][annoKey];
          }
        });
      }
      return Promise.all([
        this.annoRealTimeService.setDictionaryToFB(dico),
        this.annoRealTimeService.setAnnotationByUserToFB(responses)]);
    }
  }

  getKeyFromLabel(label: string, qKey: string, dict = this.dictionary$.value): string {
    return Object.values(dict?.[qKey] || {})
      .find((value) => value.label === label)?.key;
  }

  renameAnnotationsFromObs(newLabel: string, propagate?: boolean) {
    this.toRename$
      .pipe(switchMap(data => {
        const asArray = Object.values(data || {});
        return asArray.length ? of(asArray) : EMPTY;
      }), take(1))
      .subscribe(data => {
        const dictionary = cloneDeep(this.dictionary$.value);
        const annoByUser = cloneDeep(this.annoByUser$.value);
        const newKeyByQuestion = {};
        data.forEach(instances => {
          let sameKey = false;
          const qKey = instances[0].qKey;
          const allQuestions = propagate || instances.some(i => !i.sKey && !i.rKey && !i.userId);
          const allStatements = instances.some(i => !i.sKey && i.rKey && !i.userId) || allQuestions;
          const allRows = instances.some(i => i.sKey && !i.rKey && !i.userId) || allQuestions;
          const allUsers = instances.some(i => i.sKey && i.rKey && !i.userId) || allStatements || allRows;
          const exist = this.getKeyFromLabel(newLabel, qKey, dictionary);
          if (allRows && allStatements && allUsers && !exist) {
            newKeyByQuestion[qKey] = this.getKeyFromLabel(newLabel, qKey, dictionary) || instances[0].key;
            dictionary[qKey][newKeyByQuestion[qKey]].label = newLabel;
            sameKey = true;
          }
          if (!sameKey) {
            if (!newKeyByQuestion[qKey]) {
              newKeyByQuestion[qKey] = exist ? exist : `anno_${nanoid(7)}`;
              if (!exist) {
                dictionary[qKey][newKeyByQuestion[qKey]] =
                  {key: newKeyByQuestion[qKey], label: newLabel};
              }
            }
            Object.keys(annoByUser || {}).forEach(userId => {
              if (allUsers || instances.some(i => i.userId === +userId)) {
                Object.keys(annoByUser[userId][qKey] || {}).forEach(rKey => {
                  if (allRows || instances.some(i => i.rKey === rKey)) {
                    Object.keys(annoByUser[userId][qKey][rKey].annotations || {}).forEach(sKey => {
                      if (allStatements || instances.some(i => i.sKey === sKey)) {
                        annoByUser[userId][qKey][rKey].annotations[sKey].annoKeys =
                          Array.from(new Set(annoByUser[userId][qKey][rKey].annotations[sKey].annoKeys
                            ?.map(a => instances[0].key === a ? newKeyByQuestion[qKey] : a)));
                      }
                    });
                  }
                });
              }
            });
          }
        });
        this.annoRealTimeService.setAnnotationByUserToFB(annoByUser).then();
        this.annoRealTimeService.setDictionaryToFB(dictionary).then();
        this.toRename$.next(null);
      });
  }

  createNewAnnotation(label: string): string {
    const filterDef = this.questionFilterExpr$.value;
    const dictionary = cloneDeep(this.dictionary$.value || {});
    const key = `anno_${nanoid(7)}`;
    label = label[0].toUpperCase() + label.slice(1);
    dictionary[filterDef.key] ??= {};
    dictionary[filterDef.key][key] = {key, label};
    this.annoRealTimeService.setDictionaryToFB(dictionary).then();
    return key;
  }

  addKeywordToResponseFromKey(key: string, respRefs: Array<IResponseRefForAnnotation>, locked?: boolean) {
    const responses = cloneDeep(this.annoByUser$.value);
    respRefs.forEach(respRef => {
      responses[respRef.userId] ??= {};
      responses[respRef.userId][respRef.qKey] ??= {};
      responses[respRef.userId][respRef.qKey][respRef.rKey] ??= {annotations: {}};
      responses[respRef.userId][respRef.qKey][respRef.rKey].annotations ??= {};
      responses[respRef.userId][respRef.qKey][respRef.rKey].annotations[respRef.sKey] ??= {annoKeys: []};
      const resp: IResponseAnnotation = responses[respRef.userId]?.[respRef.qKey]?.[respRef.rKey]?.annotations?.[respRef.sKey];
      resp.annoKeys ??= [];
      if (!resp.annoKeys.includes(key)) {
        resp.annoKeys.push(key);
      }
      resp.locked = locked ?? resp.locked;
    });
    this.annoRealTimeService.setAnnotationByUserToFB(responses).then();
  }

  addKeywordToResponseFromLabel(label: string, respRef: Array<IResponseRefForAnnotation>, locked?: boolean) {
    label = label.trim();
    label = label[0].toUpperCase() + label.slice(1);
    if (label.length) {
      let key = this.getKeyFromLabel(label, this.questionFilterExpr$.value.key);
      if (!key) {
        key = this.createNewAnnotation(label);
      }
      this.addKeywordToResponseFromKey(key, respRef, locked);
    }
  }

  addWholeQuestionToRename(key: string) {
    const rename = cloneDeep(this.toRename$.value || {});
    rename[key] ??= [];
    const filterDef = this.questionFilterExpr$.value;
    if (filterDef.key) {
      if (!filterDef.statementKey?.length && !filterDef.rowKey?.length) {
        rename[key] = [{key, qKey: filterDef.key}];
      } else if (filterDef.statementKey?.length && !filterDef.rowKey?.length) {
        rename[key] = [...rename[key].filter(r =>
          r.qKey !== filterDef.key && !filterDef.statementKey.includes(r.sKey)),
          ...filterDef.statementKey.map(sKey => ({key, qKey: filterDef.key, sKey}))];
      } else if (!filterDef.statementKey?.length && filterDef.rowKey?.length) {
        rename[key] = [...rename[key].filter(r =>
          r.qKey !== filterDef.key && !filterDef.rowKey.includes(r.rKey)),
          ...filterDef.rowKey.map(rKey => ({key, qKey: filterDef.key, rKey}))];
      } else if (filterDef.statementKey?.length && filterDef.rowKey?.length) {
        rename[key] = [...rename[key].filter(r =>
          r.qKey !== filterDef.key && !filterDef.statementKey.includes(r.sKey) && !filterDef.rowKey.includes(r.rKey)),
          ...filterDef.rowKey.map(rKey => filterDef.statementKey.map(sKey => ({
            key,
            qKey: filterDef.key,
            rKey,
            sKey
          }))).flat()];
      }
    }
    this.toRename$.next(rename);
  }

  addToRename(key: string, refToRename?: Array<Partial<IToRenameRef>>) {
    const rename = cloneDeep(this.toRename$.value || {});
    rename[key] ??= [];
    refToRename?.forEach(toRename => {
      if (rename[key].some(r => r.qKey === toRename.qKey &&
        ((!r.sKey && !r.rKey && !r.userId) ||
          (r.sKey === toRename.sKey && !r.rKey && !r.userId) ||
          (!r.sKey && r.rKey === toRename.rKey && !r.userId) ||
          (r.sKey === toRename.sKey && r.rKey === toRename.rKey && !r.userId) ||
          (r.sKey === toRename.sKey && r.rKey === toRename.rKey && r.userId === toRename.userId)))) {
        return;
      }
      rename[key].push({...toRename, key} as IToRenameRef);
    });
    this.toRename$.next(rename);
    this.toastr.info(
      `${this.translate.instant('Les mots clés ont été ajoutés dans le panier de renommage-regroupement')}.`,
      `${this.affectedResponsesLength(key)} ${this.translate.instant('Ajout Renommage / Regroupement')}`,
      {
        positionClass: 'toast-top-center'
      }
    );
  }

  clearRename(key?: Array<string>) {
    if (key) {
      const rename = cloneDeep(this.toRename$.value || {});
      key.forEach(k => delete rename[k]);
      this.toRename$.next(rename);
    } else {
      this.toRename$.next(null);
    }
    return !!Object.keys(this.toRename$.value || {}).length;
  }

  affectedResponsesLength(key: string): number {
    let count = 0;
    let renamed = cloneDeep(this.toRename$.value[key] || []);
    renamed = renamed.filter(r => !r.userId);
    count = this.toRename$.value[key]?.length - renamed.length;
    if (renamed.length) {
      const allRow = renamed.every(r => !r.rKey);
      const allStatement = renamed.every(r => !r.sKey);
      const compil = this.compiledAnnotations$.value?.[renamed[0].qKey];
      Object.keys(compil || {})?.forEach((rKey) => {
        if (allRow || renamed.some(r => (r.rKey === rKey))) {
          Object.keys(compil[rKey] || {})?.forEach((sKey) => {
            if (allStatement || renamed.some(r => r.sKey === sKey && (r.rKey === rKey || allRow))) {
              count += compil[rKey][sKey][key]?.count || 0;
            }
          });
        }
      });
    }
    return count;
  }

  nextPreviousQuestion(side: 1 | -1) {
    const questions = this.metaSurveysInfosService.getSurveysMeta$(this.surveyId).value.surveyDef.questions;
    const qOrder = this.getOrderedQuestions()
      .filter(qKey => questions[qKey].rowsOrder.some(rKey =>
        Object.values(questions[qKey].rows[rKey].openCols || {}).some(o => o)));
    const index = qOrder.findIndex(qKey => qKey === this.questionFilterExpr$.value.key);
    if ((side !== -1 || index > 0) && (side !== 1 || index < qOrder.length - 1)) {
      this.changeQuestionFilterExpr(qOrder[index + side], 'key');
    }
  }
}
